// App route composition, authenticated workspace guards and public OAuth consent entry.
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AuthPage } from '@/pages/Auth';
import { OnboardingPage } from '@/pages/Onboarding';
import { HomePage } from '@/pages/Home';
import { JoinPage } from '@/pages/Join';
import { CapturePage, CaptureChooserPage } from '@/pages/Capture';
import { LedgerPage } from '@/pages/Ledger';
import { TasksPage } from '@/pages/Tasks';
import { ReceiptDetailPage } from '@/pages/ReceiptDetail';
import { CategoriesPage } from '@/pages/Categories';
import { InsightsPage } from '@/pages/Insights';
import { AdvisorPage } from '@/pages/Advisor';
import { SettingsPage, SettingsProfilePage, SettingsAppearancePage, SettingsNotificationsPage, SettingsDataPage, SettingsAboutPage } from '@/pages/Settings';
import IntegrationsPage from '@/pages/Settings/Integrations';
import { AliasesPage } from '@/pages/Aliases';
import { WorkspacePage } from '@/pages/Workspace';
import PriceCheckPage from '@/pages/PriceCheck';
import { RequireAuth, RequireWorkspace } from '@/components/guards';
import { useUserPreferences } from '@/lib/preferences';
import { SafeAreaShell } from '@/components/SafeAreaShell';
import { Toaster } from '@/components/ui/Toaster';
import { TabBar } from '@/components/TabBar';
import { CaptureFab } from '@/components/CaptureFab';
import { useNavigationDirection } from '@/lib/navigation';
import { useHardwareBack } from '@/hooks/useHardwareBack';
import { useAutoUpdate } from '@/hooks/useAutoUpdate';
import { useScrollToHash } from '@/hooks/useScrollToHash';
import { useNotificationSetup } from '@/hooks/useNotificationSetup';
import { useTheme } from '@/hooks/useTheme';
import { resolveLegacyLocation, showsMainNavigation } from '@/lib/appNavigation';

const UIDevRoute = import.meta.env.DEV
  ? lazy(() => import('@/pages/UIDevRoute'))
  : null;

// Insights drill-in subpages — lazy-loaded so the initial APK chunk stays
// lean. Each subpage inherits ?from/?to/?user_id from the hub via
// useInsightsRange and falls back to defaultRange() on direct deep-link.
const InsightsTotalPage = lazy(() => import('@/pages/insights/TotalPage'));
const InsightsSavingsPage = lazy(() => import('@/pages/insights/SavingsPage'));
const InsightsCategoriesPage = lazy(() => import('@/pages/insights/CategoriesPage'));
const InsightsMerchantsPage = lazy(() => import('@/pages/insights/MerchantsPage'));
const InsightsTrendPage = lazy(() => import('@/pages/insights/TrendPage'));
const InsightsProductsPage = lazy(() => import('@/pages/insights/ProductsPage'));
const InsightsAllowancePage = lazy(() => import('@/pages/insights/AllowanceBreakdownPage'));

// Phase 3 entity detail pages — drill from list to one item.
const InsightsCategoryDetailPage = lazy(() => import('@/pages/insights/CategoryDetailPage'));
const InsightsMerchantDetailPage = lazy(() => import('@/pages/insights/MerchantDetailPage'));
const InsightsProductDetailPage = lazy(() => import('@/pages/insights/ProductDetailPage'));

// Advisor Setup drill-in subpages — wealth, cashflow, goals split from the
// previous collapsible-sections layout (Phase 2 of mobile hub refactor).
const AdvisorWealthPage = lazy(() => import('@/pages/advisor/setup/WealthPage'));
const AdvisorCashflowPage = lazy(() => import('@/pages/advisor/setup/CashflowPage'));
const AdvisorGoalsPage = lazy(() => import('@/pages/advisor/setup/GoalsPage'));
const AdvisorGoalDetailPage = lazy(() => import('@/pages/advisor/setup/GoalDetailPage'));
const AdvisorAssetDetailPage = lazy(() => import('@/pages/advisor/setup/AssetDetailPage'));
const AdvisorTaxDeductionsPage = lazy(() => import('@/pages/advisor/setup/TaxDeductionsPage'));
const AdvisorTaxCgtPage = lazy(() => import('@/pages/advisor/setup/TaxCgtPage'));

// Advisor Plan sub-pages — drill-in from the Plan hub (Phase 4).
const AdvisorPlanAssessmentPage = lazy(() => import('@/pages/advisor/plan/AssessmentPage'));
const AdvisorPlanBudgetAlertsPage = lazy(() => import('@/pages/advisor/plan/BudgetAlertsPage'));
const AdvisorPlanCashflowPage = lazy(() => import('@/pages/advisor/plan/CashflowPage'));
const AdvisorPlanRecommendationsPage = lazy(() => import('@/pages/advisor/plan/RecommendationsPage'));
const AdvisorPlanGoalsPage = lazy(() => import('@/pages/advisor/plan/GoalsPage'));
const AdvisorPlanRisksPage = lazy(() => import('@/pages/advisor/plan/RisksPage'));

// Advisor History detail (Phase 5).
const AdvisorHistorySessionPage = lazy(() => import('@/pages/advisor/history/SessionDetailPage'));

// Smart Shopping Plan — hub + list detail (lazy to keep the initial chunk lean).
const ShoppingPage = lazy(() => import('@/pages/Shopping'));
const McpConnectionsPage = lazy(() => import('@/pages/Settings/McpConnections'));
const McpAuthorizePage = lazy(() => import('@/pages/McpAuthorize'));
const ShoppingListDetailPage = lazy(() => import('@/pages/shopping/ShoppingListDetailPage'));
const AssistantPage = lazy(() => import('@/pages/Assistant'));
const SavingsPlansPage = lazy(() => import('@/pages/advisor/setup/SavingsPlansPage'));
const SavingsPlanDetailPage = lazy(() => import('@/pages/advisor/setup/SavingsPlanDetailPage'));
const FinancePage = lazy(() => import('@/pages/finance/FinancePage'));
const financeModule = (name: keyof typeof import('@/pages/finance/ModulePages')) => lazy(async () => ({ default: (await import('@/pages/finance/ModulePages'))[name] }));
const FinanceBudgets = financeModule('BudgetsPage');
const FinanceBills = financeModule('BillsPage');
const FinanceIncome = financeModule('IncomePage');
const FinanceAssets = financeModule('AssetsPage');
const FinanceLiabilities = financeModule('LiabilitiesPage');
const FinanceNetWorth = financeModule('NetWorthPage');
const FinanceTax = financeModule('TaxPage');
const FinanceIndependence = financeModule('IndependencePage');
const FinanceHousehold = financeModule('HouseholdPage');
const FinanceCalendar = financeModule('CalendarPage');
const AssistantChat = lazy(() => import('@/pages/assistant/ChatPage'));
const AssistantOpportunities = lazy(() => import('@/pages/assistant/OpportunitiesPage'));
const AssistantProgress = lazy(() => import('@/pages/assistant/ProgressPage'));
const AssistantPreferences = lazy(() => import('@/pages/assistant/PreferencesPage'));
const InsightsDetails = lazy(() => import('@/pages/insights/DetailsPage'));
const ReconciliationPage = lazy(() => import('@/pages/ledger/ReconciliationPage'));
const GroupsPage = lazy(() => import('@/pages/ledger/GroupsPage').then(module => ({ default: module.GroupsPage })));
const GroupLedgerPage = lazy(() => import('@/pages/ledger/GroupsPage').then(module => ({ default: module.GroupLedgerPage })));

function CompatibilityRoutes() {
  const location = useLocation();
  const target = resolveLegacyLocation(location.pathname, location.search, location.hash);
  return target ? <Navigate to={target} replace state={location.state} /> : <Outlet />;
}

function LazyFallback() {
  return (
    <div className="min-h-screen bg-muted/30 p-8 text-center text-sm text-muted-foreground">
      …
    </div>
  );
}

function lazyRoute(node: ReactNode) {
  return <Suspense fallback={<LazyFallback />}>{node}</Suspense>;
}

function PreferencesBootstrap() {
  useUserPreferences();
  const client=useQueryClient();
  const restoring=useIsRestoring();
  // Refresh persisted category choices after hydration so merged legacy labels
  // cannot survive a release merely because their old cache was still fresh.
  useEffect(()=>{
    if(restoring)return;
    const refresh=()=>{if(document.visibilityState==='visible')void client.invalidateQueries({queryKey:['categories']});};
    refresh();document.addEventListener('visibilitychange',refresh);
    return()=>document.removeEventListener('visibilitychange',refresh);
  },[client,restoring]);
  return <Outlet />;
}

// SafeAreaShell already applies env(safe-area-inset-bottom) to the outer div,
// so we only need to clear the tab bar's own 56px height here. Adding a second
// inset would double-count it on phones with a non-zero bottom inset (iPhone
// home indicator / Android gesture nav).
const TAB_BAR_CLEARANCE = 'pb-20 lg:pb-0 lg:pl-56';

function AnimatedRoutes() {
  const location = useLocation();
  const direction = useNavigationDirection();
  const reduceMotion = useReducedMotion();
  useHardwareBack();
  // Reload the WebView when a newer Pages deploy ships, so the user doesn't
  // have to fully kill the Capacitor app from Recents to pick up updates.
  useAutoUpdate();
  // `#section` URL hashes scroll their target into view (alerts deep-link
  // into specific cards on /advisor/setup/wealth and /cashflow).
  useScrollToHash();
  // FCM token registration forwarding + tap-on-notification navigation.
  // No-op on web; no-op until a workspace is loaded.
  useNotificationSetup();
  // Apply the persisted theme (defaults to follow-system). Lives here so it
  // runs for pre-auth surfaces (/auth, /join) too — otherwise the login
  // page would render in the wrong scheme until the user hits a route
  // behind PreferencesBootstrap. MutationObserver in lib/native.ts watches
  // the resulting <html class> mutation and syncs the StatusBar.
  useTheme();

  const slideForward = {
    initial: { x: '100%', opacity: 0.4 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '-30%', opacity: 0.6 },
  };
  const slideBack = {
    initial: { x: '-100%', opacity: 0.4 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '30%', opacity: 0.6 },
  };
  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const variants =
    direction === 'forward' ? slideForward : direction === 'back' ? slideBack : fade;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={reduceMotion ? fade : variants}
        transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 360, damping: 32 }}
        className={'min-h-screen ' + (showsMainNavigation(location.pathname) ? TAB_BAR_CLEARANCE : '')}
      >
        <Routes location={location}>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/mcp/authorize" element={lazyRoute(<McpAuthorizePage />)} />

          {import.meta.env.DEV && UIDevRoute && (
            <Route
              path="/__ui"
              element={
                <Suspense fallback={<div className="p-6 text-sm">Loading...</div>}>
                  <UIDevRoute />
                </Suspense>
              }
            />
          )}

          <Route element={<RequireAuth />}>
            <Route element={<PreferencesBootstrap />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route element={<RequireWorkspace />}>
                <Route element={<CompatibilityRoutes />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/ledger" element={<LedgerPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/ledger/reconcile" element={lazyRoute(<ReconciliationPage />)} />
                <Route path="/ledger/reconcile/upload" element={<CapturePage mode="bank" />} />
                <Route path="/ledger/groups" element={lazyRoute(<GroupsPage />)} />
                <Route path="/ledger/groups/:id" element={lazyRoute(<GroupLedgerPage />)} />
                <Route path="/capture" element={<CaptureChooserPage />} />
                <Route path="/capture/receipt" element={<CapturePage mode="single" />} />
                <Route path="/capture/batch" element={<CapturePage mode="batch" />} />
                <Route path="/capture/manual" element={<CapturePage mode="cash" />} />
                <Route path="/capture/savings" element={<CapturePage mode="savings" />} />
                <Route path="/receipts/:id" element={<ReceiptDetailPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/insights/details" element={lazyRoute(<InsightsDetails />)} />
                <Route path="/insights/total" element={lazyRoute(<InsightsTotalPage />)} />
                <Route path="/insights/savings" element={lazyRoute(<InsightsSavingsPage />)} />
                <Route path="/insights/categories" element={lazyRoute(<InsightsCategoriesPage />)} />
                <Route path="/insights/merchants" element={lazyRoute(<InsightsMerchantsPage />)} />
                <Route path="/insights/trend" element={lazyRoute(<InsightsTrendPage />)} />
                <Route path="/insights/products" element={lazyRoute(<InsightsProductsPage />)} />
                <Route path="/insights/savings/allowance" element={lazyRoute(<InsightsAllowancePage />)} />
                <Route path="/insights/categories/:catId" element={lazyRoute(<InsightsCategoryDetailPage />)} />
                <Route path="/insights/merchants/:slug" element={lazyRoute(<InsightsMerchantDetailPage />)} />
                <Route path="/insights/products/:slug" element={lazyRoute(<InsightsProductDetailPage />)} />
                <Route path="/advisor" element={<AdvisorPage />} />
                <Route path="/assistant" element={lazyRoute(<AssistantPage />)} />
                <Route path="/assistant/chat" element={lazyRoute(<AssistantChat />)} />
                <Route path="/assistant/opportunities" element={lazyRoute(<AssistantOpportunities />)} />
                <Route path="/assistant/progress" element={lazyRoute(<AssistantProgress />)} />
                <Route path="/assistant/preferences" element={lazyRoute(<AssistantPreferences />)} />
                <Route path="/assistant/plans" element={<AdvisorPage mode="plan" />} />
                <Route path="/assistant/history" element={<AdvisorPage mode="history" />} />
                <Route path="/assistant/plans/assessment" element={lazyRoute(<AdvisorPlanAssessmentPage />)} />
                <Route path="/assistant/plans/budget-alerts" element={lazyRoute(<AdvisorPlanBudgetAlertsPage />)} />
                <Route path="/assistant/plans/cashflow" element={lazyRoute(<AdvisorPlanCashflowPage />)} />
                <Route path="/assistant/plans/recommendations" element={lazyRoute(<AdvisorPlanRecommendationsPage />)} />
                <Route path="/assistant/plans/goals" element={lazyRoute(<AdvisorPlanGoalsPage />)} />
                <Route path="/assistant/plans/risks" element={lazyRoute(<AdvisorPlanRisksPage />)} />
                <Route path="/assistant/history/:sessionId" element={lazyRoute(<AdvisorHistorySessionPage />)} />
                <Route path="/finance" element={lazyRoute(<FinancePage />)} />
                <Route path="/finance/budgets" element={lazyRoute(<FinanceBudgets />)} />
                <Route path="/finance/bills" element={lazyRoute(<FinanceBills />)} />
                <Route path="/finance/income" element={lazyRoute(<FinanceIncome />)} />
                <Route path="/finance/assets" element={lazyRoute(<FinanceAssets />)} />
                <Route path="/finance/assets/:id" element={lazyRoute(<AdvisorAssetDetailPage />)} />
                <Route path="/finance/liabilities" element={lazyRoute(<FinanceLiabilities />)} />
                <Route path="/finance/net-worth" element={lazyRoute(<FinanceNetWorth />)} />
                <Route path="/finance/tax" element={lazyRoute(<FinanceTax />)} />
                <Route path="/finance/tax/deductions" element={lazyRoute(<AdvisorTaxDeductionsPage />)} />
                <Route path="/finance/tax/cgt" element={lazyRoute(<AdvisorTaxCgtPage />)} />
                <Route path="/finance/independence" element={lazyRoute(<FinanceIndependence />)} />
                <Route path="/finance/household" element={lazyRoute(<FinanceHousehold />)} />
                <Route path="/finance/calendar" element={lazyRoute(<FinanceCalendar />)} />
                <Route path="/finance/goals" element={lazyRoute(<AdvisorGoalsPage />)} />
                <Route path="/finance/goals/:id" element={lazyRoute(<AdvisorGoalDetailPage />)} />
                <Route path="/finance/savings" element={lazyRoute(<SavingsPlansPage />)} />
                <Route path="/finance/savings/:id" element={lazyRoute(<SavingsPlanDetailPage />)} />
                <Route path="/advisor/setup/savings" element={lazyRoute(<SavingsPlansPage />)} />
                <Route path="/advisor/setup/savings/:id" element={lazyRoute(<SavingsPlanDetailPage />)} />
                <Route path="/advisor/setup/wealth" element={lazyRoute(<AdvisorWealthPage />)} />
                <Route path="/advisor/setup/cashflow" element={lazyRoute(<AdvisorCashflowPage />)} />
                <Route path="/advisor/setup/goals" element={lazyRoute(<AdvisorGoalsPage />)} />
                <Route path="/advisor/setup/goals/:id" element={lazyRoute(<AdvisorGoalDetailPage />)} />
                <Route path="/advisor/setup/wealth/assets/:id" element={lazyRoute(<AdvisorAssetDetailPage />)} />
                <Route path="/advisor/setup/tax/deductions" element={lazyRoute(<AdvisorTaxDeductionsPage />)} />
                <Route path="/advisor/setup/tax/cgt" element={lazyRoute(<AdvisorTaxCgtPage />)} />
                <Route path="/advisor/plan/assessment" element={lazyRoute(<AdvisorPlanAssessmentPage />)} />
                <Route path="/advisor/plan/budget-alerts" element={lazyRoute(<AdvisorPlanBudgetAlertsPage />)} />
                <Route path="/advisor/plan/cashflow" element={lazyRoute(<AdvisorPlanCashflowPage />)} />
                <Route path="/advisor/plan/recommendations" element={lazyRoute(<AdvisorPlanRecommendationsPage />)} />
                <Route path="/advisor/plan/goals" element={lazyRoute(<AdvisorPlanGoalsPage />)} />
                <Route path="/advisor/plan/risks" element={lazyRoute(<AdvisorPlanRisksPage />)} />
                <Route path="/advisor/history/:sessionId" element={lazyRoute(<AdvisorHistorySessionPage />)} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/profile" element={<SettingsProfilePage />} />
                <Route path="/settings/appearance" element={<SettingsAppearancePage />} />
                <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
                <Route path="/settings/data" element={<SettingsDataPage />} />
                <Route path="/settings/about" element={<SettingsAboutPage />} />
                <Route path="/settings/integrations" element={<IntegrationsPage />} />
                <Route path="/settings/mcp" element={lazyRoute(<McpConnectionsPage />)} />
                <Route path="/aliases" element={<AliasesPage />} />
                <Route path="/workspace" element={<WorkspacePage />} />
                <Route path="/price-check" element={<PriceCheckPage />} />
                <Route path="/shopping" element={lazyRoute(<ShoppingPage />)} />
                <Route path="/shopping/:id" element={lazyRoute(<ShoppingListDetailPage />)} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <SafeAreaShell>
      <AnimatedRoutes />
      <TabBar />
      <CaptureFab />
      <Toaster />
    </SafeAreaShell>
  );
}
